import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { ToastContainer, ToastType } from './Toast';
import { JiraErrorBanner } from './JiraRateLimitBanner';
import { notifyJiraError } from '../utils/jiraErrors';


interface QuickWorklogModalProps {
  ticketKey: string;
  year: number;
  month: number;
  day: number;
  projectId?: number;
  /** Total seconds already logged that day across all tickets (for fill-to-8h) */
  dayTotalSeconds?: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface Worklog {
  id: string;
  timeSpent: string;
  timeSpentSeconds: number;
  comment?: any;
  started: string;
  updated: string;
}

const pad2 = (n: number) => n.toString().padStart(2, '0');
const toDateInput = (y: number, m: number, d: number) =>
  `${y}-${pad2(m)}-${pad2(d)}`;

const QuickWorklogModal: React.FC<QuickWorklogModalProps> = ({
  ticketKey,
  year,
  month,
  day,
  projectId,
  dayTotalSeconds = 0,
  onClose,
  onSuccess
}) => {
  const [selectedDate, setSelectedDate] = useState(() => toDateInput(year, month, day));
  const [timeSpent, setTimeSpent] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingWorklogs, setExistingWorklogs] = useState<Worklog[]>([]);
  const [loadingWorklogs, setLoadingWorklogs] = useState(true);
  const [syncingWorklogs, setSyncingWorklogs] = useState(false);
  const [editingWorklog, setEditingWorklog] = useState<string | null>(null);
  const [jiraError, setJiraError] = useState<string | null>(null);

  const remainingToEight = useMemo(() => {
    const rem = Math.round(Math.max(0, 8 - dayTotalSeconds / 3600) * 100) / 100;
    return rem > 0 ? rem : 0;
  }, [dayTotalSeconds]);

  const { selYear, selMonth, selDay } = useMemo(() => {
    const [y, m, d] = selectedDate.split('-').map((x) => parseInt(x, 10));
    return {
      selYear: Number.isFinite(y) ? y : year,
      selMonth: Number.isFinite(m) ? m : month,
      selDay: Number.isFinite(d) ? d : day,
    };
  }, [selectedDate, year, month, day]);

  // Quick time presets in hours
  const quickTimes = [0.25, 0.5, 1, 1.5, 2, 2.5, 3.5, 5, 7, 7.75];

  const namedShortcuts = [
    { label: 'CR 15m', time: '0.25', comment: 'CR' },
    { label: 'Solving 30m', time: '0.5', comment: 'Solving' },
    { label: 'Implementation 2h', time: '2', comment: 'Implementation' },
  ];

  // Toast notifications
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: ToastType }>>([]);

  const addToast = (message: string, type: ToastType) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const buildStarted = () =>
    `${selYear}-${pad2(selMonth)}-${pad2(selDay)}T09:00:00.000+0000`;

  /** Load worklogs. silent=true keeps the list visible (background sync). */
  const fetchExistingWorklogs = async (silent: boolean = true) => {
    try {
      if (silent) {
        setSyncingWorklogs(true);
      } else {
        setLoadingWorklogs(true);
      }
      const response = await axios.get(`${API_URL}/api/tickets/${ticketKey}/worklogs`, {
        params: {
          year: selYear,
          month: selMonth,
          day: selDay,
          project_id: projectId,
          mine_only: true,
        },
      });
      setExistingWorklogs(response.data.worklogs || []);
      setJiraError(null);
    } catch (err) {
      console.error('Error fetching worklogs:', err);
      setJiraError(notifyJiraError(err));
    } finally {
      if (silent) {
        setSyncingWorklogs(false);
      } else {
        setLoadingWorklogs(false);
      }
    }
  };

  // Fetch when ticket or selected date changes
  useEffect(() => {
    setExistingWorklogs([]);
    setEditingWorklog(null);
    void fetchExistingWorklogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketKey, selectedDate, projectId]);

  /** After a mutation: sync modal list + parent grid in background (no UI flash). */
  const syncInBackground = () => {
    void fetchExistingWorklogs(true);
    onSuccess();
  };

  const parseHours = (value: string): number => {
    const n = parseFloat(value.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  const commentToAdf = (text: string) => {
    if (!text) return undefined;
    return {
      content: [{ content: [{ text }], type: 'paragraph' }],
      type: 'doc',
      version: 1,
    };
  };

  const handleQuickTime = (hours: number) => {
    // Just set the decimal number
    setTimeSpent(hours.toString());
  };

  const handleNamedShortcut = async (time: string, shortcutComment: string) => {
    if (editingWorklog) {
      setTimeSpent(time);
      setComment(shortcutComment);
      return;
    }

    try {
      setLoading(true);
      addToast('Logging work...', 'info');

      const started = buildStarted();

      // Optimistic row so the list doesn't flash empty
      const optimisticId = `temp-${Date.now()}`;
      setExistingWorklogs(prev => [
        ...prev,
        {
          id: optimisticId,
          timeSpent: `${time}h`,
          timeSpentSeconds: Math.round(parseHours(time) * 3600),
          comment: commentToAdf(shortcutComment),
          started,
          updated: started,
        },
      ]);

      await axios.post(`${API_URL}/api/tickets/${ticketKey}/worklog`, null, {
        params: {
          time_spent: time,
          comment: shortcutComment,
          started,
          project_id: projectId,
        },
      });

      addToast(`Logged ${shortcutComment} (${time}h) on ${ticketKey}`, 'success');
      syncInBackground();
    } catch (err: any) {
      const msg = notifyJiraError(err);
      setJiraError(msg);
      addToast(msg, 'error');
      void fetchExistingWorklogs(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorklog = async (worklogId: string) => {
    if (!window.confirm('Are you sure you want to delete this worklog?')) return;

    const previous = existingWorklogs;
    try {
      addToast('Deleting worklog...', 'info');
      // Optimistic remove
      setExistingWorklogs(prev => prev.filter(w => w.id !== worklogId));

      await axios.delete(`${API_URL}/api/tickets/${ticketKey}/worklog/${worklogId}`, {
        params: { project_id: projectId }
      });
      addToast('Worklog deleted!', 'success');
      syncInBackground();
    } catch (err: any) {
      setExistingWorklogs(previous);
      const msg = notifyJiraError(err);
      setJiraError(msg);
      addToast(msg, 'error');
    }
  };

  const handleEditWorklog = async (worklogId: string) => {
    if (!timeSpent.trim()) {
      // If empty, cancel edit
      setEditingWorklog(null);
      setTimeSpent('');
      setComment('');
      return;
    }

    const previous = existingWorklogs;
    try {
      setLoading(true);

      // Optimistic update in place
      setExistingWorklogs(prev =>
        prev.map(w =>
          w.id === worklogId
            ? {
                ...w,
                timeSpent: `${timeSpent}h`,
                timeSpentSeconds: Math.round(parseHours(timeSpent) * 3600),
                comment: comment ? commentToAdf(comment) : undefined,
              }
            : w
        )
      );
      setEditingWorklog(null);
      const savedTime = timeSpent;
      const savedComment = comment;
      setTimeSpent('');
      setComment('');

      // Build query params for FastAPI
      const params = new URLSearchParams();
      params.append('time_spent', savedTime);
      if (savedComment) {
        params.append('comment', savedComment);
      }
      if (projectId != null) {
        params.append('project_id', String(projectId));
      }

      await axios.put(`${API_URL}/api/tickets/${ticketKey}/worklog/${worklogId}?${params.toString()}`);

      syncInBackground();
    } catch (err: any) {
      setExistingWorklogs(previous);
      const msg = notifyJiraError(err);
      setJiraError(msg);
      addToast(msg, 'error');
      setEditingWorklog(null);
      setTimeSpent('');
      setComment('');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (worklog: Worklog) => {
    setEditingWorklog(worklog.id);

    // Convert timeSpentSeconds to decimal hours for editing
    const decimalHours = (worklog.timeSpentSeconds / 3600).toFixed(2);
    setTimeSpent(decimalHours);

    // Extract comment text from ADF
    if (worklog.comment?.content) {
      const text = worklog.comment.content
        .map((node: any) => node.content?.map((c: any) => c.text || '').join('') || '')
        .join('\n');
      setComment(text);
    } else {
      setComment('');
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds) return '-';
    // Convert seconds to decimal hours and remove trailing zeros
    const hours = seconds / 3600;
    return parseFloat(hours.toFixed(2)).toString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If editing, use edit handler instead
    if (editingWorklog) {
      await handleEditWorklog(editingWorklog);
      return;
    }

    if (!timeSpent.trim()) {
      addToast('Please enter time spent', 'error');
      return;
    }

    try {
      setLoading(true);
      addToast('Logging work...', 'info');

      const started = buildStarted();
      const submittedTime = timeSpent;
      const submittedComment = comment;

      // Optimistic row
      const optimisticId = `temp-${Date.now()}`;
      setExistingWorklogs(prev => [
        ...prev,
        {
          id: optimisticId,
          timeSpent: `${submittedTime}h`,
          timeSpentSeconds: Math.round(parseHours(submittedTime) * 3600),
          comment: submittedComment ? commentToAdf(submittedComment) : undefined,
          started,
          updated: started,
        },
      ]);
      setTimeSpent('');
      setComment('');

      await axios.post(`${API_URL}/api/tickets/${ticketKey}/worklog`, null, {
        params: {
          time_spent: submittedTime,
          comment: submittedComment || undefined,
          started: started,
          project_id: projectId
        }
      });

      addToast(
        `Logged ${submittedTime}h on ${ticketKey} for ${selDay}.${selMonth}.${selYear}`,
        'success'
      );
      syncInBackground();
    } catch (err: any) {
      const msg = notifyJiraError(err);
      setJiraError(msg);
      addToast(msg, 'error');
      console.error('Error logging work:', err);
      void fetchExistingWorklogs(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toast notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <div
          className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-3xl w-full"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <span className="text-xl">⏱️</span>
              <span>Log Work</span>
              {syncingWorklogs && (
                <span className="inline-flex items-center gap-1.5 text-xs font-normal text-blue-500 animate-pulse ml-2">
                  <span className="inline-block animate-spin rounded-full h-3 w-3 border-2 border-blue-500 border-t-transparent" />
                  Syncing
                </span>
              )}
            </h3>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <span className="font-mono text-blue-600 dark:text-blue-400 font-semibold text-sm">
                {ticketKey}
              </span>
              <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                <span className="text-xs text-gray-400">Datum</span>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    if (e.target.value) setSelectedDate(e.target.value);
                  }}
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white focus:border-blue-500 focus:outline-none"
                  disabled={loading}
                />
              </label>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl font-bold"
          >
            ✕
          </button>
        </div>

        {jiraError && (
          <JiraErrorBanner
            message={jiraError}
            onRetry={() => {
              setJiraError(null);
              fetchExistingWorklogs(true);
            }}
            onDismiss={() => setJiraError(null)}
          />
        )}

        {/* Add New Worklog Form */}
        <form onSubmit={handleSubmit} className="space-y-2">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Time Spent *
            </label>

            {/* Named shortcuts */}
            <div className="flex flex-wrap gap-2 mb-2">
              {remainingToEight > 0 && (
                <button
                  type="button"
                  onClick={() =>
                    handleNamedShortcut(String(remainingToEight), 'Dopočet do 8h')
                  }
                  className="px-2.5 py-1.5 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-500 hover:text-white dark:hover:bg-amber-600 text-amber-800 dark:text-amber-200 rounded text-xs font-semibold transition border border-amber-300 dark:border-amber-700"
                  disabled={loading}
                  title={`Zbývá ${remainingToEight}h do 8h za den`}
                >
                  Doplnit do 8h (+{remainingToEight}h)
                </button>
              )}
              {namedShortcuts.map((shortcut) => (
                <button
                  key={shortcut.label}
                  type="button"
                  onClick={() => handleNamedShortcut(shortcut.time, shortcut.comment)}
                  className="px-2.5 py-1.5 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-500 hover:text-white dark:hover:bg-blue-600 text-blue-700 dark:text-blue-300 rounded text-xs font-semibold transition border border-blue-300 dark:border-blue-700"
                  disabled={loading}
                >
                  {shortcut.label}
                </button>
              ))}
            </div>

            {/* Row: Comment + Time + Add button */}
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What did you work on?"
                className="flex-[5] px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:border-blue-500 focus:ring-0 text-sm"
                disabled={loading}
              />
              <input
                type="text"
                value={timeSpent}
                onChange={(e) => setTimeSpent(e.target.value)}
                placeholder="e.g. 2.5"
                className="w-40 px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:border-blue-500 focus:ring-0 text-sm"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !timeSpent.trim()}
                className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                title="Add time"
              >
                {loading ? '...' : '✅ Add'}
              </button>
            </div>

            {/* Quick time buttons - directly under Time input */}
            <div className="flex justify-end mt-2">
              <div className="flex flex-wrap gap-2" style={{ width: '250px' }}>
                {quickTimes.map((hours) => (
                  <button
                    key={hours}
                    type="button"
                    onClick={() => handleQuickTime(hours)}
                    className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-blue-500 hover:text-white dark:hover:bg-blue-600 text-gray-700 dark:text-gray-300 rounded text-xs font-medium transition border border-gray-300 dark:border-gray-600"
                    disabled={loading}
                  >
                    {hours}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
            Format: decimal hours (e.g., 1, 0.5, 2.5, 7.75). Use . or , as decimal separator.
          </p>
        </form>

        {/* Existing Worklogs - Inline Editable */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <span>📋</span>
            <span>Logged Time ({loadingWorklogs ? '...' : existingWorklogs.length})</span>
          </h4>

          {/* Initial load only — never replace the list with Loading on sync */}
          {loadingWorklogs && existingWorklogs.length === 0 ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
              Loading...
            </div>
          ) : existingWorklogs.length === 0 ? (
            <div className="text-center py-4 text-gray-400 dark:text-gray-500 text-sm italic">
              No worklogs for this day yet
            </div>
          ) : (
            <div className="space-y-2">
              {existingWorklogs.map((worklog) => {
                const isEditing = editingWorklog === worklog.id;
                const commentText = worklog.comment?.content?.map((node: any) =>
                  node.content?.map((c: any) => c.text || '').join('') || ''
                ).join('\n') || '';

                return (
                  <div
                    key={worklog.id}
                    className="flex items-center gap-3"
                  >
                    <input
                      type="text"
                      value={isEditing ? comment : commentText}
                      onChange={(e) => setComment(e.target.value)}
                      onFocus={() => !isEditing && startEdit(worklog)}
                      onBlur={(e) => {
                        // Stay in edit mode when moving between comment/time in the same row
                        const related = e.relatedTarget as HTMLElement | null;
                        if (related && e.currentTarget.parentElement?.contains(related)) return;
                        if (isEditing) handleEditWorklog(worklog.id);
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && isEditing && handleEditWorklog(worklog.id)}
                      placeholder="Add description..."
                      className="flex-[5] px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:border-blue-500 focus:ring-0 text-sm"
                    />
                    <input
                      type="text"
                      value={isEditing ? timeSpent : formatTime(worklog.timeSpentSeconds)}
                      onChange={(e) => setTimeSpent(e.target.value)}
                      onFocus={() => !isEditing && startEdit(worklog)}
                      onBlur={(e) => {
                        const related = e.relatedTarget as HTMLElement | null;
                        if (related && e.currentTarget.parentElement?.contains(related)) return;
                        if (isEditing) handleEditWorklog(worklog.id);
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && isEditing && handleEditWorklog(worklog.id)}
                      placeholder="2.5"
                      className="w-40 px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:border-blue-500 focus:ring-0 text-sm text-center"
                    />
                    <button
                      onClick={() => handleDeleteWorklog(worklog.id)}
                      className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 transition text-xl w-8"
                      title="Delete worklog"
                      disabled={worklog.id.startsWith('temp-')}
                    >
                      🗑️
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Close button */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-md font-semibold text-sm"
          >
            Close
          </button>
        </div>
        </div>
      </div>
    </>
  );
};

export default QuickWorklogModal;
